import geojsonExtent from '@mapbox/geojson-extent';
import queryString from 'query-string';

import { getValueProperty, getValuePropWithDeclaration } from '@/utils/sumTileData';
import { c, cUnits, tileAllPeriodSuffix, colorLegendTitles } from './settings';

export const infEquFilter = '<=';
export const supEquFilter = '>=';

export const getFeatureValueFromDescription = (description, feature) => (description[feature]
  ? description[feature]
  : 0);

export const sumFeaturesValue = (description, features = []) => features.reduce(
  (a, b) => {
    const currentValue = getFeatureValueFromDescription(description, b) || 0;
    return a + currentValue;
  }, 0,
);

// dataValues comes as [{ value, unit[T/U]}, {value, unit[T/U]}]
const formatTracksNoUsers = (dataValues = []) => {
  let updatedDataValue = dataValues;
  const usersValue = dataValues.find(({ unit }) => unit === 'U')?.value;
  const tracksValue = dataValues.find(({ unit }) => unit === 'T')?.value;

  const isNoUsersWithTracks = usersValue === 0 && tracksValue !== 0;

  if (isNoUsersWithTracks) {
    updatedDataValue = dataValues.map(({ value, unit }) => {
      const updatedValue = unit === 'U'
        ? null
        : value;
      return { value: updatedValue, unit };
    });
  }

  return updatedDataValue;
};

// description comes as an object { "T-1": 12, "U-1": 11 ... }
export const formatPopupDataValuesObject = (description, count, features = []) => {
  let infoList = null;

  if (description === null) {
    return ['Pas de résultats'];
  }

  // we want to include tracks if users and users if tracks
  const currentUnit = c[count];
  const unitToAdd = Object.values(c).find(item => item !== currentUnit);
  const featureToDisplay = [
    { features, unit: currentUnit },
    { features: features.map(feature => feature.replace(currentUnit, unitToAdd)), unit: unitToAdd },
  ];

  // eslint-disable-next-line no-shadow
  const dataValues = featureToDisplay.map(({ features, unit }) => {
    // sum for all features
    const value = features.reduce(
      (a, b) => {
        const currentValue = getFeatureValueFromDescription(description, b) || 0;
        return a + currentValue;
      }, 0,
    );
    return { value, unit };
  });

  infoList = formatTracksNoUsers(dataValues).map(({ value, unit }) => {
    const dataUnit = cUnits[unit];
    const formatedValue = value === null
      ? `Faible valeur de ${dataUnit}`
      : `${value.toFixed(0)} ${dataUnit}`;
    return formatedValue;
  });

  return infoList;
};

const generatePopupContentBreaks = (description, features) => {
  const tracksFeatures = features.map(featureName => `T${featureName}`);
  const durationFeatures = features.map(featureName => `D${featureName}`);
  const breaksNumber = sumFeaturesValue(description, tracksFeatures);
  const breaksMeanduration = Math.round(
    sumFeaturesValue(description, durationFeatures) / breaksNumber / 60,
  );

  return `${breaksNumber} pause(s) d'une durée moyenne de ${breaksMeanduration} min.`;
};

export const formatPopupBreaks = (description, features, activity) => {
  let desc = '';
  desc += `<p class="popup-title">${activity}</p>`;
  desc += generatePopupContentBreaks(description, features);

  return desc;
};

export const containsAudience = (aud, value) =>
  // aud come as a string if there is only one value
  // or as an array if more than one
  aud.includes(value) || aud === value;

// polygon layers specs
const polyLayers = [{
  id: 'poly',
  type: 'line',
  source: 'poly',
  paint: {
    'line-opacity': 1,
    'line-width': 7,
    'line-color': '#6b9e0e',
  },
}];

// itinerary layers specs
const polyLineLayers = [
  {
    id: 'poly-fill',
    type: 'fill',
    source: 'poly',
    'source-layer': 'data',
    minzoom: 9,
    paint: {
      'fill-opacity': 0.2,
      'fill-color': '#520c3d',
    },
  },
  {
    id: 'poly-line',
    type: 'line',
    source: 'poly',
    'source-layer': 'data',
    paint: {
      'line-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        0,
        10,
        13,
        0,
      ],
      'line-color': '#520c3d',
    },
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
  },
];

export const removePolySourcesLayers = map => {
  if (typeof map.getLayer('poly-fill') !== 'undefined') {
    map.removeLayer('poly-fill');
  }
  if (typeof map.getLayer('poly-line') !== 'undefined') {
    map.removeLayer('poly-line');
  }
  if (typeof map.getLayer('poly') !== 'undefined') {
    map.removeLayer('poly');
  }
  if (typeof map.getSource('poly') !== 'undefined') {
    map.removeSource('poly');
  }
};

// Find the index of the first symbol layer in the map style
export const getFirstSymbolId = map => {
  let firstSymbolId = null;
  const { layers } = map.getStyle();
  for (let i = 0; i < layers.length; i += 1) {
    if (layers[i].type === 'symbol') {
      firstSymbolId = layers[i].id;
      break;
    }
  }
  return firstSymbolId;
};

export const layersOrder = [
  'flux',
  'polytile',
  'polytile-border',
  'ititile-line',
  'polyfill-hover',
  'polyline-hover',
  'ititile-hover',
  'poly',
  'poly-fill',
  'poly-line',
  'suricate',
  'ecocompteur',
];

export const reorderLayers = map => {
  layersOrder.forEach((layer, index, arr) => {
    const topLayerIndex = index + 1;
    const topLayer = arr[topLayerIndex];
    if (!topLayer) return;
    // For F5 and F8, treat layers as if they were the 'flux' layer
    if (layer === 'flux' && map.getLayer('ends-points') && map.getLayer('ends-polygons') && map.getLayer(topLayer)) {
      map.moveLayer('ends-points', topLayer);
      map.moveLayer('ends-polygons', topLayer);
    } else if (map.getLayer(layer) && map.getLayer(topLayer)) {
      map.moveLayer(layer, topLayer);
    }
  });
};

export const buildQueryString = (queryObject, arrayFormat = 'comma') => queryString.stringify(queryObject, { arrayFormat, skipNull: true, sort: false });

export const handlePolygonDisplay = async (
  map, poly, type, collectionBaseUrl, isNewPoly = false,
  layerInFront = true, itiBufferBaseUrl = null, user_poly = null,
) => {
  const layers = type === 'polygon' ? polyLayers : polyLineLayers;

  if (poly) {
    removePolySourcesLayers(map);

    let firstSymbolId = null;
    if (layerInFront === false) {
      firstSymbolId = getFirstSymbolId(map);
    }

    await fetch(`${collectionBaseUrl}/${poly}`)
      .then(res => res.json())
      .then(json => {
        if (type === 'polygon') {
          map.addSource('poly', {
            type: 'geojson',
            data: json,
          });
        }

        if (type === 'line') {
          const queryStr = buildQueryString({ iti: poly, user_poly });
          map.addSource('poly', {
            type: 'vector',
            url: `${itiBufferBaseUrl}?${queryStr}`,
          });
        }

        if (layerInFront) {
          // have it on top of the map
          layers.forEach(layer => map.addLayer(layer));
        } else {
          // add this layer relativelly to the first symbol layer in the map style
          layers.forEach(layer => map.addLayer(layer, firstSymbolId));
        }

        // zoom out the see the displayed polygon / itinerary
        // only if it was just added
        if (isNewPoly) {
          const bounds = geojsonExtent(json);
          map.fitBounds(bounds, {
            duration: 0,
            padding: 30,
          });
        }
      });
  }
};

export const setOpacityListener = map => {
  const sliderElement = document.getElementById('slider');
  const sliderValueElement = document.getElementById('slider-value');

  const addOpacityEventListener = e => {
    const baseLayerId = map.getStyle().layers[0]?.id;
    if (baseLayerId) {
      // Adjust the layer opacity
      map.setPaintProperty(
        baseLayerId,
        'raster-opacity',
        parseInt(e.target.value, 10) / 100,
      );
    }

    // Value indicator
    sliderValueElement.textContent = `${e.target.value}%`;
  };

  // Set the event listener for base layer opacity
  // when it changes
  // sliderElement.removeEventListener('input', addOpacityEventListener);
  sliderElement.addEventListener('input', addOpacityEventListener);
};

export const parseActivity = rawActivities => (rawActivities ? (`${rawActivities}`).split(',') : null);

export const buildBasicUnitFilters = (min, max, valueProp) => {
  const thresholdsFilters = [];
  if (min) {
    thresholdsFilters.push([supEquFilter, valueProp, min]);
  }
  if (max) {
    thresholdsFilters.push([infEquFilter, valueProp, max]);
  }

  return thresholdsFilters;
};
export const isEmptyTime = time => time.length === 0 || (time.length === 1 && time[0] === '');
export const getTimeCategory = time => (isEmptyTime(time)
  ? ''
  : time[0].split('-')[0]);

export const hasMismatchPeriods = (time = [], time2 = []) => {
  if (isEmptyTime(time2)) { return false; }
  const timeCategory1 = getTimeCategory(time);
  const timeCategory2 = getTimeCategory(time2);

  return timeCategory1 !== timeCategory2;
};

export const shouldResetTime2 = (time1 = [], time2 = []) => {
  // only if new value is "Tous"
  if (isEmptyTime(time1)) {
    return true;
  }

  // or if we are switching time categories
  const time1category = getTimeCategory(time1);
  const time2category = getTimeCategory(time2);
  if (time1category !== time2category) {
    return true;
  }

  return false;
};

export async function resizeMapContainer () {
  const mapCanvas = document.getElementsByClassName('mapboxgl-canvas')[0];
  mapCanvas.style.width = '100%';
}
export const getFeaturesName = (time = [], unit = 'T') => time.map(t => {
  const suffix = t === ''
    ? tileAllPeriodSuffix
    : t.split('-')[1]; // eg hour-2 -> 2
  return `${unit}${suffix}`;
});

// when selecting multi periods, we are computing segments' values
// when representing tracks, we sum the values for selected periods
// when representing users, we mean the values for periods
export const getFeatureType = (countFlux = 'tracks', isComputedVar = false) => {
  const computedType = countFlux === 'tracks'
    ? 'sum'
    : 'mean';
  return isComputedVar ? computedType : 'simple';
};

export const buildMapFilterTracksUsers = (
  time, isComputedVar, filterProp,
  minUsersValueAtCurrentZoom, minTracksValueAtCurrentZoom,
  minTracksFlux, minUsersFlux,
  maxTracksFlux, maxUsersFlux,
  getFeaturesNameFunction = getFeaturesName,
  featurePrefixTracks = 'T', featurePrefixUsers = 'U',
) => {
  // Get minimum values : from the zoom level and user input
  // for both Tracks and Users
  const valueMinTracks = Math.max(minTracksValueAtCurrentZoom, minTracksFlux);
  const valueMinUsers = Math.max(minUsersValueAtCurrentZoom, minUsersFlux);

  // Get mapbox expression for Tracks and Users features
  // including the prop declaration (for example [let, 'sum']) to use in the filter expression
  const featuresTracks = getFeaturesNameFunction(time, featurePrefixTracks);
  const valuePropTracks = getValueProperty(featuresTracks, getFeatureType('tracks', isComputedVar));
  const propTracksWithDecl = getValuePropWithDeclaration(featuresTracks, valuePropTracks);

  const featuresUsers = getFeaturesNameFunction(time, featurePrefixUsers);
  const valuePropUsers = getValueProperty(featuresUsers, getFeatureType('users', isComputedVar));
  const propUsersWithDecl = getValuePropWithDeclaration(featuresUsers, valuePropUsers);

  // Build mapblibre expressions filters for Tracks and Users
  const thresholdsFiltersTracks = buildBasicUnitFilters(
    valueMinTracks, maxTracksFlux, propTracksWithDecl,
  );
  const thresholdsFiltersUsers = buildBasicUnitFilters(
    valueMinUsers, maxUsersFlux, propUsersWithDecl,
  );

  return ['all', filterProp, ...thresholdsFiltersTracks, ...thresholdsFiltersUsers];
};

export const getColorLegendTitle = countFlux => (countFlux === 'tracks'
  ? colorLegendTitles.tracks
  : colorLegendTitles.users);
